import { useEffect, useRef } from "react";

type Handler = () => void;

export interface TeletextHandlers {
  "100"?: Handler;
  "101"?: Handler;
  "102"?: Handler;
  "200"?: Handler;
  "201"?: Handler;
  "300"?: Handler;
  "0"?: Handler;
}

/**
 * Buffers digit presses for ~1s. Matches the longest prefix in `handlers`,
 * fires after the timeout, or sooner if no longer prefix is possible.
 * Ignores keystrokes while focus is in a form field.
 */
export function useTeletextKeyboard(handlers: TeletextHandlers) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    let buffer = "";
    let timer: number | undefined;

    const codes = () => Object.keys(handlersRef.current) as Array<keyof TeletextHandlers>;

    const fireAndClear = () => {
      const h = handlersRef.current[buffer as keyof TeletextHandlers];
      if (h) h();
      buffer = "";
      if (timer) {
        window.clearTimeout(timer);
        timer = undefined;
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }

      if (!/^[0-9]$/.test(e.key)) return;

      // "0" is special: only meaningful as the sole keystroke (back-to-top).
      if (buffer === "" && e.key === "0") {
        e.preventDefault();
        const h = handlersRef.current["0"];
        if (h) h();
        return;
      }

      buffer += e.key;
      e.preventDefault();

      // Does any registered code start with buffer?
      const prefixes = codes().filter((c) => c.startsWith(buffer));
      if (prefixes.length === 0) {
        buffer = "";
        return;
      }

      // Exact match and no longer extension possible — fire immediately.
      if (prefixes.length === 1 && prefixes[0] === buffer) {
        fireAndClear();
        return;
      }

      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        // On timeout, fire if buffer is an exact handler key.
        if (handlersRef.current[buffer as keyof TeletextHandlers]) {
          fireAndClear();
        } else {
          buffer = "";
          timer = undefined;
        }
      }, 1000);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      if (timer) window.clearTimeout(timer);
    };
  }, []);
}
